/**
 * Segment Destination
 * Sends events to Segment HTTP API
 */

import { BaseDestination, DestinationConfig } from './base'
import { EventData } from '../hangarx'

export interface SegmentConfig extends DestinationConfig {
  writeKey: string
  endpoint?: string
}

export class SegmentDestination extends BaseDestination {
  name = 'segment'
  private writeKey: string
  private endpoint: string
  
  constructor(config: SegmentConfig, debug: boolean = false) {
    super(config, debug)
    this.writeKey = config.writeKey
    this.endpoint = config.endpoint || 'https://api.segment.io/v1'
  }
  
  async send(events: EventData[]): Promise<void> {
    if (!this.enabled) {
      this.log('Destination disabled, skipping')
      return
    }
    
    try {
      // Segment batch API
      const batch = {
        batch: events.map(event => this.transformEvent(event)),
        context: {
          library: {
            name: '@hangarx/browser',
            version: '2.0.0'
          }
        }
      }
      
      const response = await fetch(`${this.endpoint}/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(this.writeKey + ':')}`
        },
        body: JSON.stringify(batch)
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Segment HTTP ${response.status}: ${errorText}`)
      }
      
      this.log(`Successfully sent ${events.length} events`)
    } catch (error) {
      this.logError('Failed to send events', error)
      throw error
    }
  }
  
  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    try {
      const payload = {
        type: 'identify',
        userId: userId,
        traits: traits || {},
        timestamp: new Date().toISOString()
      }
      
      const response = await fetch(`${this.endpoint}/identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(this.writeKey + ':')}`
        },
        body: JSON.stringify(payload)
      })
      
      if (!response.ok) {
        throw new Error(`Segment HTTP ${response.status}: ${response.statusText}`)
      }
      
      this.log('User identified', { userId })
    } catch (error) {
      this.logError('Failed to identify user', error)
      throw error
    }
  }
  
  async page(name?: string, properties?: Record<string, any>): Promise<void> {
    try {
      const payload = {
        type: 'page',
        name: name || document.title,
        properties: {
          url: window.location.href,
          path: window.location.pathname,
          referrer: document.referrer,
          ...properties
        },
        timestamp: new Date().toISOString()
      }
      
      const response = await fetch(`${this.endpoint}/page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(this.writeKey + ':')}`
        },
        body: JSON.stringify(payload)
      })
      
      if (!response.ok) {
        throw new Error(`Segment HTTP ${response.status}: ${response.statusText}`)
      }
      
      this.log('Page tracked', { name })
    } catch (error) {
      this.logError('Failed to track page', error)
      throw error
    }
  }
  
  private transformEvent(event: EventData): any {
    return {
      type: 'track',
      event: event.event_type,
      userId: event.user_id,
      anonymousId: event.anonymous_id,
      properties: event.properties || {},
      context: event.context || {},
      timestamp: event.context?.timestamp || new Date().toISOString()
    }
  }
}
